<template>
  <MultiSelect
    class="items-multiselect"
    :data-items="filteredItems"
    :value="value"
    :auto-close="false"
    :filterable="true"
    :tags="tags || defaultTags"
    item-render="itemSelectRender"
    header="itemSelectHeader"
    :placeholder="placeholder || 'Select Items'"
    @change="itemChanged($event)"
    @filterchange="onFilterChange($event)"
  >
    <template v-slot:itemSelectHeader>
      <div class="k-multiselect-header" @click="checkAllClicked">
        <input type="checkbox" v-model="selectAllChecked" />
        <label>Check All</label>
      </div>
    </template>
    <template v-slot:itemSelectRender="{ props }">
      <div
        :class="props.itemClass"
        class="k-multiselect-item"
        @click="(ev: any) => props.onClick(ev)"
      >
        <input
          type="checkbox"
          :key="
            props.dataItem[props.dataItem.dataItemKey] ||
            props.dataItem.Name ||
            props.dataItem
          "
          :checked="selectedItems?.includes(props.dataItem)"
        />
        <label>{{
          (textField && props.dataItem[textField]) ||
          props.dataItem.Name ||
          props.dataItem
        }}</label>
      </div>
    </template>
  </MultiSelect>
</template>

<script setup lang="ts">
import {
  MultiSelect,
  type MultiSelectChangeEvent,
  type MultiSelectFilterChangeEvent,
} from "@progress/kendo-vue-dropdowns";
import type { MultiSelectTagData } from "@progress/kendo-vue-dropdowns";
import { computed, ref, watchEffect } from "vue";

const props = defineProps<{
  items?: any[];
  value?: any[];
  tags?: MultiSelectTagData[];
  placeholder?: string;
  itemsName?: string;
  textField?: string;
  dataItemKey?: string;
}>();

const emit = defineEmits(["update:value", "tagClearClick"]);

const filteredItems = ref([...(props.items || [])]);

const selectedItems = computed({
  get() {
    return props.value;
  },
  set(value) {
    emit("update:value", value);
  },
});

const defaultTags = computed(() => {
  return selectedItems.value && selectedItems.value.length > 0
    ? [
        {
          text: `${selectedItems.value?.length} / ${props.items?.length} ${
            props.itemsName ?? "items"
          } selected`,
          data: selectedItems.value,
        },
      ]
    : [];
});

const selectAllChecked = computed(
  () =>
    filteredItems.value?.every((i) => selectedItems.value?.includes(i)) ?? false
);

const checkAllClicked = function () {
  if (!selectedItems.value) selectedItems.value = [];

  if (!selectAllChecked.value) {
    for (const item of filteredItems.value) {
      if (!selectedItems.value.includes(item)) {
        selectedItems.value.push(item);
      }
    }
  } else {
    filteredItems.value.forEach((item) => {
      const index = selectedItems.value!.findIndex(
        (i) =>
          (props.dataItemKey &&
            i[props.dataItemKey] === item[props.dataItemKey]) ||
          i.Name === item.Name ||
          i == item
      );
      if (index !== -1) {
        selectedItems.value!.splice(index, 1);
      }
    });
  }
};

const itemChanged = function (e: MultiSelectChangeEvent) {
  selectedItems.value = e.target.value;

  // Check if plants was changed by clicking on the clear value button or on the tag
  let target = e.event.target as Element;
  if (!target)
    target = (e.event as { event: PointerEvent }).event?.target as Element;
  if (target?.classList.contains("k-icon")) {
    emit("tagClearClick", e.target.value);
  }
};

const onFilterChange = function (event: MultiSelectFilterChangeEvent) {
  if (!props.items) return;
  const val = event.filter.value?.toLowerCase();
  filteredItems.value = props.items.filter(
    (i) =>
      (props.textField && i[props.textField]?.toLowerCase().includes(val)) ||
      i.Name?.toLowerCase().includes(val) ||
      (i.toLowerCase && i.includes && i.toLowerCase().includes(val))
  );
};

watchEffect(() => {
  filteredItems.value = [...(props.items || [])];
});
</script>

<style lang="scss">
.items-multiselect {
  .k-input-values {
    .k-chip-list {
      input {
        border: none;
      }
    }
  }
}

.k-animation-container {
  .k-multiselect-header {
    padding: 0.25rem 1rem;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    align-content: center;
    gap: 0.5rem;
    cursor: pointer;
    label {
      cursor: pointer;
    }
  }
  .k-list-item {
    label {
      cursor: pointer;
    }
  }
}
</style>
